import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, mergeMap, BehaviorSubject, forkJoin, map } from 'rxjs';
import { catchError, retry, switchMap, take, tap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { CartData } from './global-data-models/cart-data.model';
import { LocalStorage } from 'src/app/core/local-storage.enum';
import { UtilService } from 'src/app/shared/util.service';
import { ProductRestrictions } from './global-data-models/product-restrictions.model';
import { UserGroup } from 'src/app/module/user-group/user-group.model';
import { UserGroupService } from 'src/app/module/user-group/user-group.service';
import { UserOrderOverview } from './global-data-models/user-order-overview.model';
import { UserOverride } from './global-data-models/user-override.model';
import { AuthService } from 'src/app/core/auth/auth.service';
import { PermissionService } from 'src/app/core/permission.service';
import { CustomerAddress } from 'src/app/module/customer-address/customer-address.model';
import { ProductInformationManagementService } from '../product-information-management/product-information-management.service';
import { UserService } from 'src/app/module/user/user.service';
import { ProductComponent } from 'src/app/module/product-component/product-component.model';
import { Customer } from 'src/app/module/customer/customer.model';
import { CustomerService } from 'src/app/module/customer/customer.service';

@Injectable({
  providedIn: 'root',
})

export class GlobalDataService {

  private BASE_URL = environment.apiUrl;
  private SECURITY_URL = `${this.BASE_URL}security/UserAccount`

  public cartData: BehaviorSubject<CartData[]> = new BehaviorSubject<CartData[]>([]);
  public productRestrictions: BehaviorSubject<ProductRestrictions[]> = new BehaviorSubject<ProductRestrictions[]>([])
  public userGroupRestrictions: BehaviorSubject<UserGroup> = new BehaviorSubject<UserGroup>(new UserGroup())
  public userOrderOverview: BehaviorSubject<UserOrderOverview> = new BehaviorSubject<UserOrderOverview>(new UserOrderOverview(-1, -1, -1, new Date(), -1, -1, -1, -1, {}, -1 , -1))
  public userOverride: BehaviorSubject<UserOverride> = new BehaviorSubject<UserOverride>(new UserOverride(null))
  public currentCustomerId: BehaviorSubject<number> = new BehaviorSubject<number>(0)
  public currentUserGroupHeaderId: BehaviorSubject<number> = new BehaviorSubject<number>(0)
  public currentShipTo: BehaviorSubject<CustomerAddress> = new BehaviorSubject<CustomerAddress>(new CustomerAddress())
  public currentCatalog: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public budgetAmountLimitationActivated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public customerList: BehaviorSubject<Customer[]> = new BehaviorSubject<Customer[]>([]);
  public userGroupList: BehaviorSubject<UserGroup[]> = new BehaviorSubject<UserGroup[]>([]);

  constructor(
    private http: HttpClient,
    private utilService: UtilService,
    private authService: AuthService,
    private permissionService: PermissionService,
    private pimService: ProductInformationManagementService,
    private userService: UserService,
    private userGroupService: UserGroupService,
    private customerService: CustomerService,
  ) { }

  /**
   * Filtre la liste des catalogues pour un Superviseur :
   * pour les clients qui ont le flag onlyShowCustomerCatalogs, on masque les catalogues des User Group
   * (seuls les catalogues de type "Tous les produits clients" restent affichés).
   * Pour un employé-client, la liste est retournée telle quelle.
   */
  public filterCatalogListForSupervisor(catalogList: any[], customerList: Customer[]): any[] {
    if (this.authService.GetCurrentUser().isClientEmploye !== false) return catalogList;
    return catalogList.filter(catalog =>
      catalog.userGroupHeaderId == null ||
      !customerList.find(c => c.customerId === catalog.customerId)?.onlyShowCustomerCatalogs
    );
  }

  private getUserOrderOverview(): Observable<UserOrderOverview> {
    let userId = this.authService.GetCurrentUser().userId;
    return this.http.get<UserOrderOverview>(`${this.SECURITY_URL}/${userId}/GetUserOrderOverview`)
  }

  private getUserProductRestrictions() {
    let userId = this.authService.GetCurrentUser().userId;
    return this.http.get<any>(`${this.SECURITY_URL}/${userId}/GetUserProductRestrictions`)
  }

  private getUserOverride(): Observable<UserOverride> {
    let userId = this.authService.GetCurrentUser().userId;
    let uri = `${this.SECURITY_URL}?$filter=id eq '${userId}'` +
      `&$select=overrideEmployeMaximumAllowedAmount`

    return this.http.get<any>(uri).pipe(mergeMap(response => {
      let data: UserOverride = response.value[0]
      return of(data)
    }))
  }
  
  /*
  * fetch les info d'une adresse shipto pour un customerId et un shiptoId donné
  */
  public getCurrentShipTo(customerId: number, shipToId: string):Observable<CustomerAddress> {
    let uri = `${this.BASE_URL}Customer/CustomerShipto/${customerId}/${shipToId}`;
    return this.http.get<any>(uri).pipe( mergeMap( (response: CustomerAddress) => {
      this.setCurrentShipTo(response);
      return of(response)
    }))
  }

  /**
   * Permet d'aller chercher de l'information sur les données globale de l'usager (Info sur le client par défaut, restriction de produit, ovveride de l'usager, etc....)
   */
  public loadGlobalData(): Observable<any> {
    this.clearGlobalData();

    this.customerService.listCache().pipe(take(1)).subscribe((customers: any[]) => {
      this.customerList.next(customers);
      });

    this.userGroupService.listCache().pipe(take(1)).subscribe(ugr => {
      this.userGroupList.next(ugr);
    });

    if (this.permissionService.canLoadShopperGlobalData() && this.permissionService.mustApplyUserGroupRestriction()) {
      // si on a la permission CanBrowseCatalog on est un Client employé avec des restrictions
      return forkJoin({
        CustomerId: this.initCurrentCustomerId(),
        UserGroupHeaderId: this.initCurrentUserGroupHeaderId(),
        ProductRestriction: this.initLoadProductRestrictions(),
        UserGroupHeaderInfo: this.initloadUserGroupHeaderInfo(),
        Cart: this.initLoadCart(),
        UserOrderOverview: this.initloadUserOrderOverview(),
        UserOverride: this.initUserOverride(),
        ShipTo: this.initCurrentShipTo(),
        Catalog: this.initCurrentCatalog(),
      })
    }
    else if (this.permissionService.canLoadShopperGlobalData()) {
      // si on a la permission CanBrowseCatalog mais on est pas un Client Employé (on est un "Superviseur" ou un "Admin" sans restrictions)
      return forkJoin({
        CustomerId: this.initCurrentCustomerId(),
        UserGroupHeaderId: this.initCurrentUserGroupHeaderId(),
        Cart: this.initLoadCart(),
        UserOrderOverview: this.initloadUserOrderOverview(),
        UserOverride: this.initUserOverride(),
        ShipTo: this.initCurrentShipTo(),
        Catalog: this.initCurrentCatalog(),
      })
    }
    else {
      // si on a pas la permission CanBrowseCatalog (pas en mode Shopper)
      return this.initloadUserOrderOverview()
    }
  }

  private initLoadCart(): Observable<CartData[]> {
    let cartData: CartData[] = [];
    let ls = localStorage.getItem(LocalStorage.CART_DATA)
    if (ls != null && ls.length > 0) {
      cartData = JSON.parse(ls!) as CartData[]
    }
    this.cartData.next(cartData)
    return of(cartData)
  }

  private initLoadProductRestrictions(): Observable<ProductRestrictions[]> {
    let restrictions: ProductRestrictions[] = [];
    let ls = localStorage.getItem(LocalStorage.PRODUCT_RESTRICTIONS)

    if (ls != null && ls.length > 0) {
      restrictions = JSON.parse(ls!) as ProductRestrictions[]
      this.setProductRestrictions(restrictions)
      return of(restrictions)
    }
    else {
      return this.getUserProductRestrictions().pipe(mergeMap((products: any) => {

        for (const product of products) {
          restrictions.push(new ProductRestrictions(product.productId, product.productGroupingName, product.maximumQty))
        }

        this.setProductRestrictions(restrictions)

        return of(restrictions)
      }))
    }
  }

  private initloadUserGroupHeaderInfo(): Observable<UserGroup> {
    let ls = localStorage.getItem(LocalStorage.USERGROUP_RESTRICTIONS)
    let info: UserGroup

    if (ls != null && ls.length > 0) {
      info = JSON.parse(ls!) as UserGroup
      this.setUserGroupRestrictions(info)
      return of(info)
    }
    else {
      let userGroupId = this.authService.GetCurrentUser().currentUserGroupHeaderId;
      return this.http.get<any>(`${this.BASE_URL}Customer/UserGroupHeader/${userGroupId}`).pipe(mergeMap((ugr: UserGroup) => {
        info = ugr;
        this.setUserGroupRestrictions(info)
        return of(info)
      }))
    }
  }

  public initloadUserOrderOverview(): Observable<UserOrderOverview> {
    let ls = localStorage.getItem(LocalStorage.USER_ORDER_OVERVIEW)
    let info: UserOrderOverview

    if (ls != null && ls.length > 0) {
      info = JSON.parse(ls!) as UserOrderOverview
      let infoCorrected = this.UserOrderOverviewCorrection(info)
      this.setUserOrderOverview(infoCorrected)
      return of(infoCorrected)
    }
    else {
      return this.getUserOrderOverview().pipe(mergeMap(uoo => {
        let uooCorrected = this.UserOrderOverviewCorrection(uoo)
        this.setUserOrderOverview(uooCorrected)
        return of(uooCorrected)
      }))
    }
  }

  /**
   * Si on reçoit null dans amountRemaining et maxAvailable, ont doit corriger pour mettre infinity via le constructeur
   * @param uoo UserOrderOverview
   * @returns UserOrderOverview corrigé
   */
  private UserOrderOverviewCorrection(uoo: UserOrderOverview): UserOrderOverview {
    let uooCorrected: UserOrderOverview = new UserOrderOverview(
      uoo.maxAvailableAmount,
      uoo.amountRemaining,
      uoo.actualPurchasedAmount,
      uoo.minDateToCheck,
      uoo.nbOrderTotal,
      uoo.nbOrderNotApproved,
      uoo.nbOrderCompleted,
      uoo.nbOrderInCartMode,
      uoo.qtyConsumedByProductId,
      uoo.productComponentDeductedFromBudget,
      uoo.shippingFeesDeductedFromBudget
    )
    return uooCorrected
  }

  private initUserOverride(): Observable<UserOverride> {
    let ls = localStorage.getItem(LocalStorage.USER_OVERRIDE)
    let info: UserOverride

    if (ls != null && ls.length > 0) {
      info = JSON.parse(ls!) as UserOverride
      this.setUserOverride(info)
      return of(info)
    }
    else {
      return this.getUserOverride().pipe(mergeMap(data => {
        this.setUserOverride(data)
        return of(data)
      }))
    }
  }

  /*
  * on ne fait pas de call API ici. Si c'est vide parce qu'il faut que la sélection du shipToId soit faites
  */
  private initCurrentShipTo(): Observable<CustomerAddress | null> {
    let ls = localStorage.getItem(LocalStorage.CURRENT_SHIPTO)
    let info: CustomerAddress

    if (ls != null && ls.length > 0) {
      info = JSON.parse(ls!) as CustomerAddress
      this.setCurrentShipTo(info)
      return of(info)
    }
    else {
      return of(null);
    }
  }

  /*
  * on ne fait pas de call API ici. on va juste chercher la valeur dans le localstorage et on assigne on retourne la valeur
  */
  private initCurrentCatalog(): Observable<any | null> {
    let ls = localStorage.getItem(LocalStorage.CURRENT_CATALOG)
    let info: any

    if (ls != null && ls.length > 0) {
      info = JSON.parse(ls!) as any
      this.setCurrentCatalog(info)
      return of(info)
    }
    else {
      return of(null);
    }
  }

  private initCurrentCustomerId() {
    let ls = localStorage.getItem(LocalStorage.CURRENT_CUSTOMER_ID)
    let info: number

    if (ls != null && ls.length > 0) {
      info = JSON.parse(ls!) as number
      this.setCurrentCustomerId(info)
      return of(info)
    }
    else {
      let x = this.authService.GetCurrentUser().currentCustomerId;
      this.setCurrentCustomerId(x)
      return of(x)
    }
  }

  private initCurrentUserGroupHeaderId() {
    let ls = localStorage.getItem(LocalStorage.CURRENT_USERGROUP_ID)
    let info: number

    if (ls != null && ls.length > 0) {
      info = JSON.parse(ls!) as number
      this.setCurrentUserGroupHeaderId(info)
      return of(info)
    }
    else {
      let x = this.authService.GetCurrentUser().currentUserGroupHeaderId;
      this.setCurrentUserGroupHeaderId(x)
      return of(x)
    }
  }

  /*
  * Fetch la liste des prix clients pour les produits item du catalog
  */
  public GetRealCustomerListPrices(allCatalogProducts: any): Observable<any> {
    // construire un array des productItemIds
    let listIds = allCatalogProducts.map((a: any) => a.product.defaultProductItemId);
    // fetcher les prix
    return this.getProductItemsPrice(listIds).pipe(
      map((priceList: any) => {
        for (let product of allCatalogProducts) {
          // trouver le prix dans la liste
          const priceItem = priceList.find((x: any) => x.productItemId === product.product.defaultProductItemId);
          if (priceItem) {
            // on set le prix et stock dans le produit
            product.product.productItemListPrice = priceItem.lastInforCSDPricing;
            product.product.inforCSD_StockInfo_NetQuantityAvailable =
              priceItem.inforCSD_StockInfo_NetQuantityAvailable;
          } else {
            console.warn(`Price not found for productItemId: ${product.product.defaultProductItemId}`);
          }
        }
        return allCatalogProducts; // on retourne la liste mise à jour (peut etre meme pas nécessaire si c'ESt un pointeur sur l'objet A VOIR)
      })
    );
  }

  /**
   * Récupère les prix des items du catalogue pour le client et le shipToId
   * applique l'override du usergroup sur le shipto si applicable
   * @param listOfItemsIds liste des ids des items à récupérer
   * @returns Observable avec les prix des items
   */
  public getProductItemsPrice(listOfItemsIds: number[]): Observable<any> {

    var customerId = this.currentCustomerId.getValue();
    var shipToId = this.currentShipTo.getValue().shipToId;

    // ne rien faire si la liste est vide
    if (!listOfItemsIds || listOfItemsIds.length === 0 ||customerId === 0) {
      return of([]);
    }

    //failsafe pour ne pas faire de call inutile
    if (shipToId === '') {
      return of();
    }

    return this.getItemsCustomerPrices(customerId, shipToId, listOfItemsIds);
  }

  /*
  * fetch des prix des produits items pour le client et shipToId donné
  */
  private getItemsCustomerPrices(customerId: number, shipToId: string, itemIds: number[]): Observable<any[]> {
    let uri = `${this.BASE_URL}Product/Catalog/GetProductItemPriceForCustomerv2/${customerId}/${shipToId}`;
    return this.http.post<any>(uri, itemIds).pipe(
      map((reponse: any) => Object.values(reponse))
    );
  }

  /*
  * fetch les prix pour les produit composants seulement (bypass de la vérification si le produits fait parti du client/usergroup)
  */
  public getProductComponentPrices(itemIds: number[]): Observable<any[]> {
    var customerId = this.currentCustomerId.getValue();
    var shipToId = this.currentShipTo.getValue().shipToId;
    let uri = `${this.BASE_URL}Product/Catalog/GetProductComponentPrice/${customerId}/${shipToId}`;
    return this.http.post<any>(uri, itemIds).pipe(
      map((reponse: any) => Object.values(reponse))
    );
  }

  // calcule le total des prix des product components pour le produit item courant
  public getProductComponentTotalPrice(productComponentList: ProductComponent[]): number {
    let total: number = 0;
    productComponentList.forEach(pc => {
      // on ajoute le prix seulement si includePriceInParent est false (sinon le prix est déjà inclus dans le parent)
      if (pc.childProductComponentType === 'AddProductItemInNewLine'&& pc.includePriceInParent == false && pc.price != null && pc.childQuantityMultiplicator != null) {
        total += pc.price * pc.childQuantityMultiplicator;
      }
    });
    return total;
  }

  // SECTION CART CRUD methods

  /**
   * Ajoute conditionnellement au panier dépdendemment si on respecte les conditions
   */

  public addToCart(product: any): boolean {
    if (this.permissionService.canAddToCartWithoutRestriction() ||
        this.canProductBeAddedToCard(product, product.quantity)) {
      // on ajoute si on est superviseur ou si on passe les restrictions
      this.addToCartData(product);
      return true;
    }
    else 
      return false;
  }

  /*
  * ajoute le produit au données du panier et appel pour écrire dans le local storage et setter le behavior subject
  */
  private addToCartData(product: any): void {

    let currentCart: CartData[] = this.cartData.getValue();

    // créer un index
    var index = Date.now();
    product.index = index;

    // on ajoute la nouvelle ligne
    currentCart.push(product);

    //On met à jour le cart
    this.setCart(currentCart);
  }

  /*
   * Met à jour la quantité si possible
   * appeler par les input de quantité du cart
   * on passe carrément un pointeur vers cartItemData alors on peut modifier directement dedans
   */
  public updateCartItemQuantity(cartItemData: any, newQuantity: any): boolean {
    let cartData = this.cartData.getValue();
    let quantityToAdd = newQuantity - cartItemData.quantity;

    if (this.canProductBeAddedToCard(cartItemData, quantityToAdd)) {
      if (newQuantity != null)
        cartItemData.quantity = newQuantity;
      this.setCart(cartData);
      return true;
    } 
    else 
      return false;
  }

  /*
   * Met à jour la note si la valeur n'est pas nulle
   * appeler par le champs Note du cart
   * on passe carrément un pointeur vers cartItemData alors on peut modifier directement dedans
   */
  public updateCartItemNote(cartItemData: any, note01: any): boolean {
    let cartData = this.cartData.getValue();
    if (note01 != null) {
      cartItemData.note01 = note01;
      this.setCart(cartData);
      return true;
    }
    else 
      return false;
  }

  /*
   * enlève un item du cartData
   * @param index
   */
  public removeCartItem(index: number): void {
    let cartData = this.cartData.getValue()
    this.setCart(cartData.filter(x => x.index !== index))
  }

  /**
   * sauvegarde le cartData dans observable et localstorage
   * @param index
   */
  private setCart(cartData: CartData[]): void {
    this.cartData.next(cartData);
    localStorage.setItem(LocalStorage.CART_DATA, JSON.stringify(cartData))
  }

  public clearCart(): void {
    this.cartData.next([]);
    localStorage.removeItem(LocalStorage.CART_DATA)
  }

  // user group restrictions
  public setUserGroupRestrictions(ugr: UserGroup): void {
    this.userGroupRestrictions.next(ugr);
    localStorage.setItem(LocalStorage.USERGROUP_RESTRICTIONS, JSON.stringify(ugr))
  }

  public clearUserGroupRestrictions(): void {
    this.userGroupRestrictions.next(new UserGroup())
    localStorage.removeItem(LocalStorage.USERGROUP_RESTRICTIONS)
  }

  // product restriction

  public setProductRestrictions(productRestrictions: ProductRestrictions[]): void {
    this.productRestrictions.next(productRestrictions)
    localStorage.setItem(LocalStorage.PRODUCT_RESTRICTIONS, JSON.stringify(productRestrictions))
  }

  public clearProductRestrictions(): void {
    this.productRestrictions.next([])
    localStorage.removeItem(LocalStorage.PRODUCT_RESTRICTIONS)
  }

  // user order overview

  public setUserOrderOverview(userOrderOverview: UserOrderOverview): void {
    this.userOrderOverview.next(userOrderOverview)
    localStorage.setItem(LocalStorage.USER_ORDER_OVERVIEW, JSON.stringify(userOrderOverview))
  }

  public clearUserOrderOverview(): void {
    this.userOrderOverview.next(new UserOrderOverview(-1, -1, -1, new Date(), -1, -1, -1, -1, {}, -1, -1))
    localStorage.removeItem(LocalStorage.USER_ORDER_OVERVIEW)
  }

  // user override
  
  public setUserOverride(userOverride: UserOverride): void {
    this.userOverride.next(userOverride)
    localStorage.setItem(LocalStorage.USER_OVERRIDE, JSON.stringify(userOverride))
  }

  public clearUserOverride(): void {
    this.userOverride.next(new UserOverride(null))
    localStorage.removeItem(LocalStorage.USER_OVERRIDE)
  }

  // current ship to 

  public setCurrentShipTo(address: CustomerAddress): void {
    this.currentShipTo.next(address);
    localStorage.setItem(LocalStorage.CURRENT_SHIPTO, JSON.stringify(address));
  }

  public clearCurrentShipTo(): void {
    this.currentShipTo.next(new CustomerAddress());
    localStorage.removeItem(LocalStorage.CURRENT_SHIPTO);
  }

  // current customer id

  public setCurrentCustomerId(customerId: number): void {
    this.currentCustomerId.next(customerId)
    localStorage.setItem(LocalStorage.CURRENT_CUSTOMER_ID, JSON.stringify(customerId))
  }

  public clearCurrentCustomerId(): void {
    this.currentCustomerId.next(0)
    localStorage.removeItem(LocalStorage.CURRENT_CUSTOMER_ID)
  }

  // user group header id

  public setCurrentUserGroupHeaderId(userGroupHeaderId: number): void {
    this.currentUserGroupHeaderId.next(userGroupHeaderId)
    localStorage.setItem(LocalStorage.CURRENT_USERGROUP_ID, JSON.stringify(userGroupHeaderId))
  }

  public clearCurrentUserGroupHeaderId(): void {
    this.currentUserGroupHeaderId.next(0)
    localStorage.removeItem(LocalStorage.CURRENT_USERGROUP_ID)
  }

  // user group header (aka restrictions) 

  public setCurrentUserGroupHeader(userGroupHeaderId: number): void {
    this.userGroupService.listCache().pipe(take(1)).subscribe(ugr => {
      if (ugr && ugr.length >= 1) {
        let userGroup = ugr.find(x => x.userGroupHeaderId === userGroupHeaderId);
        if (userGroup) {
          this.setCurrentUserGroupHeaderId(userGroupHeaderId);
          this.setUserGroupRestrictions(userGroup);
        }
      }
    });
  }

  // catalog

  public setCurrentCatalog(catalog: any): void {
    localStorage.setItem(LocalStorage.CURRENT_CATALOG, JSON.stringify(catalog))
    this.currentCatalog.next(catalog);
  }

  public clearCurrentCatalog(): void {
    this.currentCatalog.next(null)
    localStorage.removeItem(LocalStorage.CURRENT_CATALOG)
  }
  
  // customer list

  public clearCustomerList(): void {
    this.customerList.next([]);
    localStorage.removeItem(LocalStorage.CACHE_CUSTOMER);
  }

  // user group list

  public clearUserGroupList(): void {
    this.userGroupList.next([]);
    localStorage.removeItem(LocalStorage.CACHE_USER_GROUP);
  }

  // clear global cache data

  public clearGlobalData(): void {
    this.clearCart();
    this.clearProductRestrictions();
    this.clearUserGroupRestrictions();
    this.clearUserOrderOverview();
    this.clearUserOverride();
    this.clearCurrentShipTo();
    this.clearCurrentCatalog();
    this.clearCurrentCustomerId();
    this.clearCurrentUserGroupHeaderId();
    this.clearCustomerList();
    this.clearUserGroupList();
  }

  //SECTION  Restrictions methods
  /**
   * Cette méthode valide que le produit peut être ajouté au panier en validant la liste des restrictions (budget, quantité, etc)
   * 
   * IMPORTANT: On doit appeler les méthodes initloadUserGroupHeaderInfo() et initUserOverride() avant de pouvoir utiliser cette méthode.
   * @param itemId
   * @param quantityToAdd Quantité à rajouter au cart
   * @returns un booléenne si la modification ou ajout d'item est légale ou non
   */
  private canProductBeAddedToCard(product: any, quantityToAdd: number): boolean {

    // Les superviseur ont toujours le droits d'ajouter à leur guise.
    if (this.permissionService.canAddToCartWithoutRestriction()) {
      return true;
    }

    let remainingBudget = this.getRemainingBudget();
    let remainingQuantity = this.getRemainingQuantity(product.productId);
    let productRes: ProductRestrictions = this.productRestrictions.getValue().filter(x => x.productId === product.productId)[0];
    
    // deduire les prix des product components si applicable
    let ugr = this.userGroupRestrictions.getValue();
    if (ugr.deductProductComponentFromBudget) 
      remainingBudget -= (quantityToAdd * (product.itemPrice  + product.productComponentPrice));
    else
      remainingBudget -= (quantityToAdd * product.itemPrice);

    // check si on dépasse le budget restant
    if (remainingBudget < 0) {
      this.popupAlertOverMaxBudget(-remainingBudget);
      return false;
    }

    // si aucune restriction de QTÉ n'a été placée sur le produit ou le regroupement OU si un produit à la demande
    if (productRes.maxQuantity == null || product.isProductOnRequest) {
      return true;
    }

    // Validation de la quantité restante
    if (quantityToAdd <= remainingQuantity) {
      return true;
    }
    else {

      if (productRes.productGroupingName == null || productRes.productGroupingName === '') {
        this.popupAlertOverMaxProductQuantity(remainingQuantity);
      }
      else {
        this.popupAlertOverMaxProductGroupQuantity(remainingQuantity);
      }

      return false;
    }

  }

  private getQuantityAlreadyOrded(productId: number): number {
    let uoo: object = this.userOrderOverview.getValue().qtyConsumedByProductId
    let uoo_keys = Object.keys(uoo)
    let uoo_values = Object.values(uoo)
    let value = uoo_values[uoo_keys.indexOf(`${productId}`)]

    return value ? value : 0
  }

  /**
   *
   * @returns Le budget restant
  */
  public getRemainingBudget(): number {
    //console.log('getRemainingBudget');
    //Fix pour éviter que le header envoit des erreurs de itemprice is undefined lorsuq'on fait un ngDoCheck
    if (!this.permissionService.canSeeBudget()) {
      this.budgetAmountLimitationActivated.next(false);
      return Infinity //hey oui infinity est un nombre légal en Javascript qui l'aurait cru
    }

    let ugr = this.userGroupRestrictions.getValue()
    let uo = this.userOverride.getValue()
    let uoo = this.userOrderOverview.getValue()

    // Si les users n'ont pas de restrictions budgétaires alors on retourne infini
    if (ugr.maximumAllowedAmount == null && uo.overrideEmployeMaximumAllowedAmount == null) {
      this.budgetAmountLimitationActivated.next(false);
      return Infinity
    }

    let remainingBudget: number = uoo.amountRemaining;
    let cartData: CartData[] = this.cartData.getValue();
    let subtotal: number = 0;

    try {
      for (const line of cartData) {
        if (ugr.deductProductComponentFromBudget)
          subtotal += (line.quantity * (line.itemPrice + line.productComponentPrice));
        else
          subtotal += (line.quantity * line.itemPrice);
      }
      remainingBudget -= subtotal;

      // déduire les frais de transport si applicable
      // TODO: modificer car le frais de transport ne vient pas du user group
      /*
      if (ugr.deductTransportFeeFromBudget && subtotal < ugr.minimumAmountToSkipTransportFee) {
        remainingBudget -= ugr.transportFeeAmount
      }
      */

    }
    // Fail safe on retourne 0 pour bloquer le reste du système
    catch (error) {
      this.budgetAmountLimitationActivated.next(true);
      return 0
    }

    this.budgetAmountLimitationActivated.next(true);
    return remainingBudget > 0 ? remainingBudget : 0;
  }

  /**
   *
   * @returns La valeur du panier 
   * LEGACY : à revoir dans checkout
  */
  public getCartTotalValue(): Observable<number> {
    let value = 0;
    let cartData = this.cartData.getValue();
    cartData.forEach(cartLine => {
      try {

        value += (cartLine.quantity * cartLine.itemPrice)

      } catch (error) {
        //on ne fais rien à cause que le header componnent trigger trop souvent et envoi trop d'erreur dans la console
      }
    });
    return of(value);
  }

  public getListOfAllPossibleFilteringProperties(): string[] {
    return ['productItemColor',
      'productItemFootwearProtectionSize',
      'productItemFootwearProtectionWidth',
      'productItemProtectiveClothingSize',
      'productItemHandProtectionSize',
      'productItemHeadProtectionSize',
      'productItemHearingProtectionSize',
      'productItemFallProtectionSize',
      'productIteminstrumentationSize',
      'productItemErgonomicSize',
      'productItemRespiratoryProtectionSize',
      'productItemLockoutDiameter',
      'productItemDiameter',
      'productItemFirstAidSize',
      'productItemProtectiveClothingLegLength',
      'productItemFallProtectionLength',
      'productItemLensColor',
      'productIteminstrumentationLength',
      'productItemHand',
      'productItemFirstAidFormat',
      'productItemEnvironmentFormat',
      'productIteminstrumentationFormat',
      'productItemEyewearFormat',
      'productItemQtyPerBox',
      'productItemHearingQtyPerBox',
      'productItemDiopter',
      'productItemPressure',
      'productItemFrameColor',
      'productItemThickness',
      'productItemManagementAndStorageOfHazardousDimensions',
      'productItemFirstAidDimensions',
      'productItemEyewearDimension',
      'productItemFootwearLength',
      'productItemHandProtectionLength',
      'productItemSafetyEyewearSize',
      'productItemLadderHeight',
      'productItemFireExtinguishersCapacity',
      'productItemFireExtinguishersUlcRating',
      'productItemPoweredAirAndSuppliedAirLength',
      'productItemClothingLength',
      'productItemWeightCapacity',
      'productItemAchorageType',
      'productItemLifelineHook',
      'productItemFallProtectionDimensions',
      'productItemLanguage',
      'productItemSignsAndDisplayMaterial',
      'productItemSignsAndDisplayDimension',
      'productItemHeadband',
      'productItemLengthFeet',
      'productItemFireWidthin',
      'productItemAtpv',
      'productItemBracketType',
      'productItemCollar',
      'productItemTrafficConeSize',
      'productItemSizeinitialimport'
    ]
  }

  /**
   *
   * @param productId
   * @returns la quantité restante du produit ou de son groupe s'il en possède un.
  */
  public getRemainingQuantity(productId: number): number {

    let _productRestrictions = this.productRestrictions.getValue();
    let _cart = this.cartData.getValue();

    let productGroupingName: string | null = null;
    let filteredRestriction = _productRestrictions.filter(x => x.productId === productId);

    // Failsafe, si les données globales sont mal chargées alors on retourne 0
    if (filteredRestriction.length === 0) {
      return 0;
    }

    productGroupingName = filteredRestriction[0].productGroupingName;

    let quantityAlreadyAdded: number = 0;
    let filteredProductRes = _productRestrictions.filter(y => y.productGroupingName === productGroupingName)
    let quantityAlreadyOrded = 0;

    if (productGroupingName == null || productGroupingName === '') {
      //On va rajouter les quanitité des items dans le cart
      for (const line of _cart.filter(y => y.productId === productId)) {
        quantityAlreadyAdded += line.quantity
      }
      //On rajoute les quantités déjà commandées
      quantityAlreadyOrded = this.getQuantityAlreadyOrded(productId);
    }
    //Cela va chercher les quantités des autres produit ayant le même grouping product name
    else {
      for (const res of filteredProductRes) {
        //On va rajouter les quanitité des items dans le cart ayant le même product grouping name
        for (const line of _cart.filter(y => y.productId === res.productId)) {
          quantityAlreadyAdded += line.quantity
        }
        //On rajoute les quantités déjà commandées des autres produits du même product grouping name
        quantityAlreadyOrded += this.getQuantityAlreadyOrded(res.productId)
      }
    }

    let maxQuantity = filteredRestriction[0].maxQuantity

    //si aucune restriction n'est imposéee
    if (maxQuantity == null) {
      return Infinity
    }
    else {
      let remainingQuantity: number = maxQuantity - quantityAlreadyAdded - quantityAlreadyOrded

      if (remainingQuantity < 0) {
        remainingQuantity = 0
      }

      return remainingQuantity
    }
  }

  public canUserSeeProductPrice(): Observable<boolean> {
    // Si c'est un employé normale, alors on doit check dans les restrictions de son user group
    if (this.permissionService.mustApplyUserGroupRestriction()) {
      return this.userGroupRestrictions.pipe(
        mergeMap((ugr: UserGroup) => {
          return of(!ugr.mustHidePrices)
        })
      )
    }
    //Les superviseurs peuvent toujours voire les prix
    else {
      return of(true)
    }
  }

  public canUserSeeProductPrice_sync(): boolean {
    // Si c'est un employé normale, alors on doit check dans les restrictions de son user group
    if (this.permissionService.mustApplyUserGroupRestriction()) {
      var ugr = this.userGroupRestrictions.getValue();
      return !ugr.mustHidePrices;
    }
    //Les superviseurs peuvent toujours voire les prix
    else {
      return true;
    }
  }

  /**
   *
   * @returns Booléen de si on peut voir la bannière dans le header du solde restant
   */
  public canUserSeeHeaderBudget(): Observable<boolean> {
    if (this.permissionService.canSeeBudget()) {
      return this.canUserSeeProductPrice()
    }
    //On ne veut pas afficher le solde restant aux non client_employé,
    //puisqu'ils n'ont pas de solde
    else {
      return of(false)
    }
  }

  /**
   *
   * @returns Booléen de si on peut voir la bannière dans le header du solde restant
   */
  public canUserSeeHeaderBudget_sync(): boolean {
    if (this.permissionService.canSeeBudget()) {
      return this.canUserSeeProductPrice_sync();
    }
    else {
      return false;
    }
  }

  private popupAlertOverMaxBudget(overBudget: number): void {
    let title = this.utilService.getPropertyTranslated("ATTENTION - Vous avez dépassé votre budget", "WARNING - You have exceeded your budget");
    let msg = this.utilService.getPropertyTranslated(`Vous avez dépassé votre budget. \n Dépassement de la marge: ${overBudget.toFixed(2)} $`, `You went over your allowed budget. \n You're $${overBudget.toFixed(2)} over budget`);
    this.utilService.alert(title, msg);
  }

  private popupAlertOverMaxProductGroupQuantity(remainingQuantity: number): void {
    let title = this.utilService.getPropertyTranslated("ATTENTION - Vous avez dépassé votre limite", "WARNING - You have exceeded your limit");
    let msg = this.utilService.getPropertyTranslated(`Vous avez dépassé la quantité maximale allouée pour ce groupe de produits, la quantité restante est de ${remainingQuantity}.`,
      `You went over the maximum quantity allowed for this group of items, the remaining quantity is ${remainingQuantity}.`);
    this.utilService.alert(title, msg);
  }

  private popupAlertOverMaxProductQuantity(remainingQuantity: number): void {
    let title = this.utilService.getPropertyTranslated("ATTENTION - Vous avez dépassé votre limite", "WARNING - You have exceeded your limit");
    let msg = this.utilService.getPropertyTranslated(`Vous avez dépassé la quantité maximale allouée pour ce produit, la quantité restante est de ${remainingQuantity}.`,
      `You went over the maximum quantity allowed for this item, the remaining quantity is ${remainingQuantity}.`);
    this.utilService.alert(title, msg);
  }

  //TEMPORAIRE! 2023-08-18 - fait en PROD HotFix - à remplacer par un test similaire, mais avec la nouvelle méthjode, dans le PermissionService...
  public canUserSeeCartGlobalNote(): boolean {
    return true // 2023-09-06 : À La demande de Marie, on doit réactiver la note du panier pour les clients employés.

  }



  /** Permet de faire un login et de rafraichir les données globales
   *
  */
  loginAndRefreshData(username: string, password: string): Observable<any> {
    //On doit d'abord faire un login
    return this.authService.login(username, password)
      .pipe(
        //On doit ensuite rafraichir les données globales
        switchMap((x) => {
          return this.refreshGlobalUserData(x.userId);
        }),
        tap(() => {
          //si on veut faire quelquechose apres tout ça [attention!  un bon délais à cause de RefreshGlobalUserData!]
        }),
      );
  }


  /** Permet de DÉBUTER l'impersonation et de rafraichir les données globales
   *
   */
  ImpersonateAndRefreshData(UserIdToImpersonate: string): Observable<any> {
    //On doit d'abord faire un 'impersonate' (donc on prend l'identitié d'un usager passé en param)
    return this.authService.ImpersonateUser(UserIdToImpersonate)
      .pipe(
        switchMap((x) => {
          //On doit ensuite rafraichir les données globales
          return this.refreshGlobalUserData(x.userId);
        }),
        tap(() => {
          //si on veut faire quelquechose apres tout ça [attention!  un bon délais à cause de RefreshGlobalUserData!]
        }),
      );
  }


  /** Permet de CESSER l'impersonation et de rafraichir les données globales
   *
   */
  StopImpersonationAndRefreshData(): Observable<any> {
    //On doit d'abord stopper l'impersonation
    return this.authService.StopImpersonation()
      .pipe(
        switchMap((x) => {
          //On doit ensuite rafraichir les données globales
          return this.refreshGlobalUserData(x.userId);
        }),
        tap(() => {
          //si on veut faire quelquechose apres tout ça [attention!  un bon délais à cause de RefreshGlobalUserData!]
        }),
      );
  }


  /** Permet de faire un refresh des données globales (CVL, donnée du user connecté, liste des )
   *
   */
  public refreshGlobalUserData(newUserId: string): Observable<any> {
    return this.userService.read(newUserId)
      .pipe(
        tap((x) => {
          this.authService.addUserNames(x);
        }),
        switchMap(() => {
          return this.loadGlobalData();
        }),
        switchMap(() => {
          return this.pimService.initLoadCvlFields();;
        }),
        switchMap(() => {
          return this.pimService.initLoadCvlData();
        }),
      );
  }

  /*
  * Retourne un objet contenant le total du panier et la quantité d'item dans le panier
  * la fonction tient compte du user group restriction deductProductComponentFromBudget
  */

  public getCartTotalAndQuantity(): { total: number, itemQuantity: number } {

    let deductProductComponentFromBudget = this.userGroupRestrictions.getValue().deductProductComponentFromBudget;
    
    let total = 0;
    let itemQuantity = 0;
    let cart = this.cartData.getValue();
    for (let cartLine of cart) {
      try {
        if (cartLine.item == null) {
          continue; // Skip this iteration if productItem is null (safety)
        } 
        if (deductProductComponentFromBudget && cartLine.productComponentPrice != null)
          total += (cartLine.quantity * (cartLine.itemPrice + cartLine.productComponentPrice));
        else
          total += (cartLine.quantity * cartLine.itemPrice);
        itemQuantity += cartLine.quantity;
      } catch (error) {
        console.log('Error in getCartTotalAndQuantity', error);
      }
    }
    return { total, itemQuantity };
  }

  /*
  * Retourne un objet contenant le soustotal, le total (avec ou sans livraison), le budget restant, la valeur à afficher pour la livraison
  */

  public getClientEmployeCurrentTotals(cart: CartData[], ugr: UserGroup, uoo: UserOrderOverview, shipTo: CustomerAddress) {

    let subtotal = 0;
    let total = 0;
    let itemQuantity = 0;
    let transportFeeAmountFormatted = '';

    // calcul du sous total

    for (let cartLine of cart) {
      try {
        if (cartLine.item == null) {
          continue; // Skip this iteration if productItem is null (safety)
        }
        // déduit ou non le total des product components
        if (ugr.deductProductComponentFromBudget && cartLine.productComponentPrice != null)
          subtotal += (cartLine.quantity * (cartLine.itemPrice + cartLine.productComponentPrice));
        else
          subtotal += (cartLine.quantity * cartLine.itemPrice);
        itemQuantity += cartLine.quantity;
      } catch (error) {
        console.log('Error in getCartSubTotalInfo', error);
      }
    }

    // Calcul des frais de transport et du total

    const standardTransportFee = shipTo.transportFeeAmount ?? 0;
    const reducedTransportFee = shipTo.freeTransportFeeAmount ?? 0;
    const minimumForReducedFee = shipTo.minimumAmountForFreeTransportFee ?? 0;
    
    // Déterminer les frais de transport applicables
    let appliedTransportFee = 0;
    
    if (subtotal >= minimumForReducedFee) {
      // Le seuil pour les frais réduits est atteint
      appliedTransportFee = reducedTransportFee;
    } else {
      // Frais de transport standard
      appliedTransportFee = standardTransportFee;
    }
    
    // Formatter l'affichage des frais de transport
    if (appliedTransportFee === 0) {
      transportFeeAmountFormatted = this.utilService.getPropertyTranslated("Gratuit", "Free");
    } else {
      transportFeeAmountFormatted = this.utilService.putDollarSignByLocal(appliedTransportFee.toFixed(2));
    }
    
    // Calculer le total selon si on déduit ou non du budget
    if (ugr.deductTransportFeeFromBudget) {
      total = subtotal + appliedTransportFee;
    } else {
      total = subtotal;
    }

    // budget restant
    let budgetTotal = uoo.amountRemaining;
    let remainingBudget = parseFloat((budgetTotal - total).toFixed(2));
    remainingBudget = remainingBudget > 0 ? remainingBudget : 0;  

    return { subtotal, total, remainingBudget, itemQuantity, transportFeeAmountFormatted, appliedTransportFee };
  }

}
